import { httpsCallable } from "firebase/functions"
import { fbFunctions } from "../firebase"

export interface MinAvgMax {
  min: number
  avg: number
  max: number
}

export interface AdminStats {
  totalUsers: number
  activeUsers: number
  totalBoards: number
  orphanedBoards: number
  memberlessBoards: number
  sessionsPerBoard: MinAvgMax
  itemsPerSession: MinAvgMax
  boardsWithNotes: number
  totalNotes: number
  boardsPerUser: MinAvgMax
  notesPerBoard: MinAvgMax
  newSessionsThisWeek: number
  usersByTier: { free: number; supporter: number; pro: number }
  newUsersThisWeek: number
}

export async function fetchAdminStats(): Promise<AdminStats | null> {
  const fn = httpsCallable<void, { stats: AdminStats | null }>(fbFunctions, "getAdminStats")
  const result = await fn()
  return result.data.stats
}

export interface DiscreteOptionsMigrationResult {
  itemsMigrated: number
  pollsMigrated: number
  dryRun: boolean
}

export async function runDiscreteOptionsMigration(dryRun: boolean): Promise<DiscreteOptionsMigrationResult> {
  const fn = httpsCallable<{ dryRun: boolean }, DiscreteOptionsMigrationResult>(fbFunctions, "migrateDiscreteOptionsToUids")
  const result = await fn({ dryRun })
  return result.data
}

// --- Admin Tools (Phase 6) ---

export interface AdminUserData {
  uid: string
  email: string | null
  name: string | null
  entitlements: Record<string, unknown> | null
  transactions: Array<Record<string, unknown>>
}

export async function adminLookupUser(query: { uid?: string; email?: string }): Promise<AdminUserData> {
  const fn = httpsCallable<{ uid?: string; email?: string }, AdminUserData>(fbFunctions, "adminLookupUser")
  const result = await fn(query)
  return result.data
}

export interface AdminRefundRequest {
  targetUid: string
  type: "supporter" | "pro"
}

export interface AdminRefundResult {
  success: boolean
  refundedAmount: number
}

export async function adminRefund(request: AdminRefundRequest): Promise<AdminRefundResult> {
  const fn = httpsCallable<AdminRefundRequest, AdminRefundResult>(fbFunctions, "adminRefund")
  const result = await fn(request)
  return result.data
}

export interface AdminSetEntitlementRequest {
  targetUid: string
  tier: "supporter" | "pro"
  active: boolean
  expiresAt?: number | null
}

export async function adminSetEntitlement(request: AdminSetEntitlementRequest): Promise<{ success: boolean }> {
  const fn = httpsCallable<AdminSetEntitlementRequest, { success: boolean }>(fbFunctions, "adminSetEntitlement")
  const result = await fn(request)
  return result.data
}

export async function adminSetTierFeatures(tier: string, features: string[]): Promise<{ success: boolean }> {
  const fn = httpsCallable<{ tier: string; features: string[] }, { success: boolean }>(fbFunctions, "adminSetTierFeatures")
  const result = await fn({ tier, features })
  return result.data
}
